import { useRef, useState } from "react";
import { Upload, Download, Loader2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CASE_AREAS } from "@/lib/caseAreas";

// Lote 172 — importar processos de uma planilha (Excel/CSV): escolher o arquivo, conferir a prévia, importar.
// Nada é gravado antes de "Importar"; o servidor revalida tudo na gravação.
const SIT = {
  novo: { rotulo: "Novo", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  existe: { rotulo: "Já cadastrado", cls: "bg-stone-100 text-stone-700 border-stone-300" },
  problema: { rotulo: "Problema", cls: "bg-red-50 text-red-800 border-red-200" },
};
const areaRotulo = (v) => (v ? (CASE_AREAS.find((a) => a.value === v)?.label || v) : "");
const juizo = (r) => [areaRotulo(r.case_area), r.comarca, r.vara].filter(Boolean).join(" · ");
const partes = (r) => [r.party_author, r.party_defendant].filter(Boolean).join(" x ");

function baixar(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function ImportarProcessos({ open, onOpenChange, onImported }) {
  const [etapa, setEtapa] = useState("arquivo");       // arquivo | previa | resultado
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState("");
  const [previa, setPrevia] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [criarClientes, setCriarClientes] = useState(true);
  const [completar, setCompletar] = useState(true);
  const [gravando, setGravando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef(null);

  const reiniciar = () => { setEtapa("arquivo"); setErro(""); setPrevia(null); setFiltro("todos"); setCriarClientes(true); setCompletar(true); setResultado(null); setLendo(false); setGravando(false); };
  const fechar = (o) => {
    if (!o) { const feito = etapa === "resultado"; reiniciar(); onOpenChange(false); if (feito) onImported && onImported(); return; }
    onOpenChange(true);
  };

  const lerArquivo = async (file) => {
    if (!file) return;
    setErro(""); setLendo(true);
    try {
      const fd = new FormData(); fd.append("arquivo", file);
      const { data } = await http.post("/processos/importar/previa", fd, { headers: { "Content-Type": undefined }, timeout: 60000 });
      setPrevia(data); setFiltro("todos"); setEtapa("previa");
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não consegui ler a planilha. Tente de novo.");
    } finally { setLendo(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const baixarModelo = async () => {
    try {
      const r = await http.get("/processos/importar/modelo", { responseType: "blob" });
      baixar(r.data, "iapauta-modelo-importacao.xlsx");
    } catch (e) { toast.error("Não foi possível baixar o modelo"); }
  };

  const importar = async () => {
    const novos = previa.linhas.filter((r) => r.situacao === "novo");
    setGravando(true);
    try {
      const { data } = await http.post("/processos/importar", {
        linhas: novos.map((r) => ({ linha: r.linha, numero: r.numero, apelido: r.apelido, cliente: r.cliente, case_area: r.case_area, comarca: r.comarca,
          vara: r.vara, party_author: r.party_author, party_defendant: r.party_defendant, valor_causa: r.valor_causa, data_ajuizamento: r.data_ajuizamento, situacao_arquivo: r.situacao_arquivo })),
        criar_clientes: criarClientes, completar,
      }, { timeout: 120000 });
      setResultado(data); setEtapa("resultado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível importar. Nada foi gravado.");
    } finally { setGravando(false); }
  };

  const baixarProblemas = () => {
    const lista = previa.linhas.filter((r) => r.situacao === "problema");
    const esc = (t) => `"${String(t ?? "").replace(/"/g, '""')}"`;
    const csv = "﻿" + ["Linha;Número;Motivo", ...lista.map((r) => [r.linha, esc(r.numero || ""), esc(r.motivo || "")].join(";"))].join("\n");
    baixar(new Blob([csv], { type: "text/csv;charset=utf-8" }), "importacao-problemas.csv");
  };

  const resumo = previa?.resumo;
  const linhasVis = previa ? previa.linhas.filter((r) => filtro === "todos" || r.situacao === filtro) : [];
  const Chip = ({ id, label, n, cls }) => (
    <button type="button" data-testid={`importar-chip-${id}`} aria-pressed={filtro === id} onClick={() => setFiltro(filtro === id ? "todos" : id)}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${cls} ${filtro === id ? "ring-2 ring-stone-700" : ""}`}>
      {n} {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={fechar}>
      <DialogContent data-testid="importar-dialog" className="w-[calc(100vw-1rem)] max-w-4xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        {etapa === "arquivo" && (
          <div data-testid="importar-etapa-arquivo">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Importar processos de uma planilha</DialogTitle>
              <DialogDescription>Excel (.xlsx) ou CSV, uma linha por processo. Nada é gravado antes de você conferir a prévia.</DialogDescription>
            </DialogHeader>
            <label htmlFor="importar-arquivo-input" data-testid="importar-dropzone"
              onDragOver={(e) => { e.preventDefault(); setArrastando(true); }} onDragLeave={() => setArrastando(false)}
              onDrop={(e) => { e.preventDefault(); setArrastando(false); lerArquivo(e.dataTransfer.files?.[0]); }}
              className={`mt-4 flex cursor-pointer flex-col items-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center ${arrastando ? "border-gold-600 bg-gold-100" : "border-gold-500 bg-[#fffaf0]"}`}>
              {lendo ? <Loader2 className="w-6 h-6 animate-spin text-stone-700" /> : <Upload className="w-6 h-6 text-stone-800" strokeWidth={1.75} />}
              <b className="text-sm text-stone-900">{lendo ? "Lendo a planilha…" : <><span className="hidden sm:inline">Arraste a planilha aqui</span><span className="sm:hidden">Escolher planilha</span></>}</b>
              <span className="text-xs text-stone-600">{lendo ? "" : <><span className="hidden sm:inline">ou <u>escolha um arquivo</u> · </span>até 500 linhas</>}</span>
              <input id="importar-arquivo-input" ref={inputRef} data-testid="importar-arquivo-input" type="file" className="sr-only" disabled={lendo}
                accept=".xlsx,.xlsm,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => lerArquivo(e.target.files?.[0])} />
            </label>
            {erro && <p role="alert" data-testid="importar-erro" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{erro}</p>}
            <p className="mt-4 text-xs text-stone-700"><b>Colunas reconhecidas</b> (pelo título; a ordem não importa):<br />
              <span className="text-stone-500">Número* · Apelido · Cliente · Tipo de ação · Comarca · Vara · Partes (Autor x Réu) · Valor da causa · Data de ajuizamento · Situação ("arquivado" entra já arquivado)</span></p>
            <p className="mt-3 rounded-lg border border-gold-300 bg-gold-100/60 px-3 py-2 text-xs text-stone-800">
              Só o <b>número do processo</b> é obrigatório. O que faltar (vara, comarca, tipo, partes) o app completa sozinho depois, pelo DataJud e pelas publicações. A planilha da opção <b>Exportar planilha</b> também serve de modelo.
            </p>
            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" data-testid="importar-modelo" onClick={baixarModelo} className="flex items-center gap-1.5 text-xs font-semibold text-stone-800 underline underline-offset-2">
                <Download className="w-3.5 h-3.5" /> Baixar planilha-modelo
              </button>
              <Button type="button" variant="outline" data-testid="importar-cancelar" onClick={() => fechar(false)}>Cancelar</Button>
            </div>
          </div>
        )}

        {etapa === "previa" && previa && (
          <div data-testid="importar-previa">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Conferir antes de importar</DialogTitle>
              <DialogDescription className="flex items-center gap-1.5"><FileSpreadsheet className="w-4 h-4 shrink-0" /> <span data-testid="importar-arquivo-nome">{previa.arquivo}{previa.aba ? ` · aba “${previa.aba}”` : ""} · {resumo.total} linhas</span></DialogDescription>
            </DialogHeader>
            <div className="mt-3 flex flex-wrap gap-2">
              <Chip id="novo" n={resumo.novos} label={resumo.novos === 1 ? "novo" : "novos"} cls="bg-emerald-50 text-emerald-800 border-emerald-200" />
              <Chip id="existe" n={resumo.existem} label="já cadastrados (ficam de fora)" cls="bg-stone-100 text-stone-700 border-stone-300" />
              <Chip id="problema" n={resumo.problemas} label="com problema (ficam de fora)" cls="bg-red-50 text-red-800 border-red-200" />
            </div>

            {resumo.arquivados > 0 && (
              <p className="mt-2 text-xs text-stone-600" data-testid="importar-arquivados">
                <b>{resumo.arquivados}</b> {resumo.arquivados === 1 ? "entra" : "entram"} já <b>arquivados</b>, como a planilha indica (a consulta fica pausada; dá para reativar em cada processo).
              </p>
            )}
            <div className="mt-3 hidden sm:block overflow-x-auto" data-testid="importar-tabela">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[11px] uppercase tracking-wide text-stone-500 border-b border-stone-200">
                  <th className="py-1.5 pr-2">Linha</th><th className="pr-2">Número</th><th className="pr-2">Cliente / Apelido</th><th className="pr-2">Partes</th><th className="pr-2">Tipo · Comarca · Vara</th><th>Situação</th>
                </tr></thead>
                <tbody>
                  {linhasVis.map((r) => (
                    <tr key={r.linha} data-testid={`importar-linha-${r.linha}`} className="border-b border-dashed border-stone-200 align-top">
                      <td className="py-2 pr-2 tabular-nums text-stone-500">{r.linha}</td>
                      <td className="pr-2 tabular-nums whitespace-nowrap">{r.numero || <span className="text-stone-400">—</span>}</td>
                      <td className="pr-2">{r.cliente || "—"}{r.apelido && <div className="text-stone-500">{r.apelido}</div>}</td>
                      <td className="pr-2">{partes(r) || "—"}</td>
                      <td className="pr-2">{juizo(r) || (r.situacao === "novo" ? <span className="text-stone-400">completa pelo DataJud</span> : <span className="text-stone-400">—</span>)}</td>
                      <td><span className={`inline-block rounded-full border px-2 py-px font-semibold ${SIT[r.situacao].cls}`}>{SIT[r.situacao].rotulo}</span>
                        {r.cliente_novo && <span className="ml-1 text-stone-500">cliente novo</span>}
                        {r.situacao === "novo" && r.situacao_arquivo && <span className="ml-1 rounded-full border border-stone-300 bg-stone-100 px-2 py-px font-semibold text-stone-700" data-testid={`importar-arquivado-${r.linha}`}>{r.situacao_arquivo === "extinto" ? "Entra extinto" : "Entra arquivado"}</span>}
                        {r.motivo && <div className="mt-0.5 text-stone-500">{r.motivo}</div>}
                        {(r.avisos || []).map((a) => <div key={a} className="mt-0.5 text-amber-700">{a}</div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 sm:hidden space-y-2" data-testid="importar-cartoes">
              {linhasVis.map((r) => (
                <div key={r.linha} data-testid={`importar-cartao-${r.linha}`} className={`rounded-lg border bg-white p-2.5 ${r.situacao === "problema" ? "border-red-200" : "border-stone-200"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <b className="text-xs tabular-nums break-all">{r.numero || `Linha ${r.linha}`}</b>
                    <span className={`shrink-0 rounded-full border px-2 py-px text-[11px] font-semibold ${SIT[r.situacao].cls}`}>{r.situacao === "existe" ? "Já existe" : SIT[r.situacao].rotulo}</span>
                  </div>
                  {(r.cliente || r.apelido) && <div className="text-xs text-stone-800">{[r.cliente, r.apelido].filter(Boolean).join(" · ")}{r.cliente_novo && <span className="text-stone-500"> (cliente novo)</span>}</div>}
                  {juizo(r) && <div className="text-xs text-stone-500">{juizo(r)}</div>}
                  {r.situacao === "novo" && r.situacao_arquivo && <div className="text-xs font-semibold text-stone-700" data-testid={`importar-arquivado-${r.linha}`}>{r.situacao_arquivo === "extinto" ? "Entra extinto" : "Entra arquivado"}</div>}
                  {r.motivo && <div className="text-xs text-stone-500">{r.motivo}</div>}
                  {(r.avisos || []).map((a) => <div key={a} className="text-xs text-amber-700">{a}</div>)}
                </div>
              ))}
            </div>
            {linhasVis.length === 0 && <p className="mt-4 text-center text-xs text-stone-500">Nenhuma linha neste filtro.</p>}

            <div className="mt-4 border-t border-stone-200 pt-3">
              {resumo.clientes_novos > 0 && (
                <label className="flex items-start gap-2 text-xs text-stone-800 cursor-pointer">
                  <input type="checkbox" data-testid="importar-criar-clientes" className="mt-0.5 h-4 w-4 accent-stone-700" checked={criarClientes} onChange={(e) => setCriarClientes(e.target.checked)} />
                  <span>Criar {resumo.clientes_novos === 1 ? "o cliente" : <>os <b>{resumo.clientes_novos} clientes</b></>} que ainda não existe{resumo.clientes_novos === 1 ? "" : "m"} (só o nome)</span>
                </label>
              )}
              <label className="mt-1.5 flex items-start gap-2 text-xs text-stone-800 cursor-pointer">
                <input type="checkbox" data-testid="importar-completar" className="mt-0.5 h-4 w-4 accent-stone-700" checked={completar} onChange={(e) => setCompletar(e.target.checked)} />
                <span>Completar vara, comarca, tipo e partes pelo <b>DataJud</b> e pelas publicações depois de importar</span>
              </label>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <button type="button" data-testid="importar-trocar-arquivo" onClick={reiniciar} className="text-xs font-semibold text-stone-700 underline underline-offset-2">Escolher outro arquivo</button>
                <div className="flex w-full sm:w-auto gap-2">
                  <Button type="button" variant="outline" data-testid="importar-cancelar" onClick={() => fechar(false)}>Cancelar</Button>
                  <Button type="button" data-testid="importar-confirmar" disabled={gravando || resumo.novos === 0} onClick={importar} className="flex-1 sm:flex-none bg-stone-700 hover:bg-stone-800 text-white">
                    {gravando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {resumo.novos === 0 ? "Nada novo para importar" : `Importar ${resumo.novos} ${resumo.novos === 1 ? "processo" : "processos"}`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {etapa === "resultado" && resultado && (
          <div data-testid="importar-resultado">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{resultado.criados} {resultado.criados === 1 ? "processo importado" : "processos importados"}</DialogTitle>
              <DialogDescription>
                {[resultado.clientes_criados > 0 && `${resultado.clientes_criados} ${resultado.clientes_criados === 1 ? "cliente criado" : "clientes criados"}`,
                  resultado.arquivados > 0 && `${resultado.arquivados} ${resultado.arquivados === 1 ? "entrou arquivado" : "entraram arquivados"}`,
                  resultado.existiam > 0 && `${resultado.existiam} já existiam (ignorados)`,
                  resultado.problemas.length > 0 && `${resultado.problemas.length} com problema`].filter(Boolean).join(" · ") || "Tudo certo."}
              </DialogDescription>
            </DialogHeader>
            {resultado.completando && <p className="mt-3 text-xs text-stone-600" data-testid="importar-completando">O app já começou a consultar o DataJud, aos poucos, para completar o que faltou. Cada preenchimento aparece no histórico do processo.</p>}
            <div className="mt-4 flex items-center justify-between gap-3">
              {resultado.problemas.length > 0 ? (
                <button type="button" data-testid="importar-baixar-problemas" onClick={baixarProblemas} className="flex items-center gap-1.5 text-xs font-semibold text-stone-800 underline underline-offset-2">
                  <Download className="w-3.5 h-3.5" /> Baixar lista dos {resultado.problemas.length} com problema
                </button>
              ) : <span />}
              <Button type="button" data-testid="importar-ver-processos" onClick={() => fechar(false)} className="bg-stone-700 hover:bg-stone-800 text-white">Ver processos</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
